import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { DigitsOnlyDirective } from '../../core/directives';
import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import { AuthService } from '../auth/auth.service';
import { ExportService } from '../../core/services/export.service';
import {
  defaultBranchFilterId,
  isBranchFilterLocked,
  matchesBranchFilter,
} from '../../core/utils/branch-scope.util';
import type {
  InventoryItem,
  StockTransfer,
} from '../../core/models/inventory.models';
import { confirmAction } from '../../core/utils/confirm.util';
import { currentTimestamp, formatDate } from '../../core/utils/format.util';
import { environment } from '../../environments/environment';
import {
  assertCanTake,
  parsePositiveInt,
  remainingAvailable,
} from '../../core/utils/stock-qty.util';

type TransferStatus = StockTransfer['status'];

interface TransferLineDraft {
  itemId: string;
  code: string;
  name: string;
  quantity: number;
  available: number;
}

interface PickerItemView extends InventoryItem {
  available: number;
  canTransfer: boolean;
  unavailableReason: string;
}

@Component({
  selector: 'app-stock-transfer',
  standalone: true,
  imports: [FormsModule, CommonModule, DigitsOnlyDirective],
  templateUrl: './stock-transfer.component.html',
  styleUrl: './stock-transfer.component.css',
})
export class StockTransferComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  private readonly auth = inject(AuthService);

  readonly timestamp = currentTimestamp;
  readonly formatDate = formatDate;
  readonly allTransfers = this.inventory.allStockTransfers;
  /** From = home branch. To = any branch in the same company. */
  readonly fromBranches = this.settings.scopedBranches;
  readonly toBranches = this.settings.activeCompanyBranches;
  readonly branches = this.settings.activeCompanyBranches;
  readonly locations = this.settings.activeCompanyLocations;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));

  readonly search = signal('');
  readonly fromBranchFilter = signal('all');
  readonly toBranchFilter = signal('all');
  readonly statusFilter = signal('all');
  readonly page = signal(1);
  readonly pageSize = 10;

  readonly showModal = signal(false);
  readonly editingTransfer = signal<StockTransfer | null>(null);
  readonly saving = signal(false);

  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  readonly form = signal({
    fromBranchId: '',
    fromLocationId: '',
    toBranchId: '',
    toLocationId: '',
    transferDate: new Date().toISOString().slice(0, 10),
    status: 'pending' as TransferStatus,
    notes: '',
    referenceNumber: '',
  });

  readonly lines = signal<TransferLineDraft[]>([]);
  readonly selectedItemId = signal('');
  readonly selectedQty = signal(1);
  readonly itemSearch = signal('');

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      const defaultFilter = defaultBranchFilterId(this.auth, branches);
      this.fromBranchFilter.set(defaultFilter);
      // Keep "to" open so branch users see transfers out of their branch (not only internal moves).
      this.toBranchFilter.set('all');
    });
  }

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.refreshFromApi();
    }
  }

  readonly filteredTransfers = computed(() => {
    const q = this.search().trim().toLowerCase();
    const fromFilter = this.fromBranchFilter();
    const toFilter = this.toBranchFilter();
    const status = this.statusFilter();
    const fromBranches = this.fromBranches();
    const toBranches = this.toBranches();
    const locked = this.branchFilterLocked();

    return this.allTransfers().filter((t) => {
      const fromName = (t.fromBranch || '').toLowerCase();
      const toName = (t.toBranch || '').toLowerCase();
      const fromBlob =
        `${fromName} ${t.fromLocation || ''} ${t.fromLocationName || ''}`.toLowerCase();
      const toBlob =
        `${toName} ${t.toLocation || ''} ${t.toLocationName || ''}`.toLowerCase();

      const matchesSearch =
        !q ||
        t.transferNumber.toLowerCase().includes(q) ||
        fromBlob.includes(q) ||
        toBlob.includes(q) ||
        (t.itemName || '').toLowerCase().includes(q);

      const matchesFrom =
        fromFilter === 'all' ||
        (locked
          ? this.transferInvolvesBranch(fromFilter, t, fromBranches)
          : this.transferMatchesBranch(
              fromFilter,
              t.fromBranch,
              t.fromBranchId,
              fromBlob,
              fromBranches,
            ));
      const matchesTo =
        toFilter === 'all' ||
        this.transferMatchesBranch(
          toFilter,
          t.toBranch,
          t.toBranchId,
          toBlob,
          toBranches,
        );
      const matchesStatus = status === 'all' || t.status === status;
      return matchesSearch && matchesFrom && matchesTo && matchesStatus;
    });
  });

  readonly stats = computed(() => {
    const all = this.branchVisibleTransfers();
    return {
      total: all.length,
      completed: all.filter((t) => t.status === 'completed').length,
      inTransit: all.filter(
        (t) => t.status === 'in-transit' || t.status === 'pending',
      ).length,
      cancelled: all.filter((t) => t.status === 'cancelled').length,
    };
  });

  /** Transfers visible to a branch-locked user (from or to their branch). */
  readonly branchVisibleTransfers = computed(() => {
    const all = this.allTransfers();
    if (!this.branchFilterLocked()) {
      return all;
    }
    const branchId = defaultBranchFilterId(this.auth, this.branches());
    if (!branchId || branchId === 'all') {
      return all;
    }
    return all.filter((t) =>
      this.transferInvolvesBranch(branchId, t, this.branches()),
    );
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredTransfers().length / this.pageSize)),
  );
  readonly paginatedTransfers = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filteredTransfers().slice(start, start + this.pageSize);
  });
  readonly paginationStart = computed(() =>
    this.filteredTransfers().length === 0
      ? 0
      : (this.page() - 1) * this.pageSize + 1,
  );
  readonly paginationEnd = computed(() =>
    Math.min(this.page() * this.pageSize, this.filteredTransfers().length),
  );
  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  readonly fromLocations = computed(() => {
    const branchId = this.form().fromBranchId;
    return this.locations().filter((l) => !branchId || l.branchId === branchId);
  });

  readonly toLocations = computed(() => {
    const branchId = this.form().toBranchId;
    return this.locations().filter((l) => !branchId || l.branchId === branchId);
  });

  readonly availableItems = computed(() => {
    const search = this.itemSearch().trim().toLowerCase();
    const selected = new Set(this.lines().map((l) => l.itemId));
    const fromBranchId = this.form().fromBranchId;
    const fromLocationId = this.form().fromLocationId;

    return this.inventory
      .allItems()
      .map((item) => this.toPickerItem(item, fromBranchId, fromLocationId))
      .filter((item) => {
        if (selected.has(item.id)) return false;
        if (!search) return true;
        return (
          item.code.toLowerCase().includes(search) ||
          item.name.toLowerCase().includes(search) ||
          item.category.toLowerCase().includes(search)
        );
      });
  });

  private toPickerItem(
    item: InventoryItem,
    branchId: string,
    locationId: string,
  ): PickerItemView {
    const branch = this.branches().find((b) => b.id === branchId);
    const location = this.locations().find((l) => l.id === locationId);

    const stockRows = this.inventory.allStockSnapshots().filter((snap) => {
      const inventoryItemId =
        (snap as { inventoryItemId?: string }).inventoryItemId || snap.id;
      return inventoryItemId === item.id || snap.itemCode === item.code;
    });

    const sumAvailable = (rows: typeof stockRows) =>
      rows.reduce(
        (sum, snap) =>
          sum + remainingAvailable(snap.quantity, snap.reservedQuantity ?? 0),
        0,
      );

    const branchRows = branchId
      ? stockRows.filter((snap) =>
          this.matchesSnapshotBranch(snap, branchId, branch),
        )
      : stockRows;

    const atSource = branchRows.filter(
      (snap) =>
        !locationId || this.matchesSnapshotLocation(snap, locationId, location),
    );

    const available = sumAvailable(atSource);

    const suggestions = this.inventory.suggestStockLocations(item.id, {
      branchId,
      locationId,
    });
    const suggestionText = this.inventory.formatStockSuggestions(suggestions);

    let unavailableReason = '';
    if (item.status === 'inactive' || item.status === 'discontinued') {
      unavailableReason = `Item is ${item.status}`;
    } else if (!branchId || !locationId) {
      unavailableReason = 'Select from branch and location first';
    } else if (available <= 0) {
      unavailableReason = suggestionText
        ? `Not at this location. Available at ${suggestionText}`
        : 'No available quantity at from location';
    }

    return {
      ...item,
      available,
      unavailableReason,
      canTransfer: !unavailableReason && available > 0,
    };
  }

  private matchesSnapshotBranch(
    snap: { branch?: string; branchId?: string },
    branchId: string,
    branch?: { id: string; name: string },
  ): boolean {
    if (!branchId) {
      return true;
    }
    const snapBranchId = (snap.branchId ?? '').trim();
    const snapBranch = (snap.branch ?? '').trim().toLowerCase();
    const branchName = (branch?.name ?? '').trim().toLowerCase();
    return (
      snapBranchId === branchId ||
      snapBranch === branchName ||
      snapBranch === branchId.toLowerCase()
    );
  }

  private matchesSnapshotLocation(
    snap: { location?: string; locationId?: string },
    locationId: string,
    location?: { id: string; name: string },
  ): boolean {
    if (!locationId) {
      return true;
    }
    const snapLocId = (snap.locationId ?? '').trim();
    const snapLoc = (snap.location ?? '').trim().toLowerCase();
    const locName = (location?.name ?? '').trim().toLowerCase();
    return (
      snapLocId === locationId ||
      snapLoc === locName ||
      (!!locName && snapLoc.includes(locName)) ||
      (!!snapLoc && locName.includes(snapLoc))
    );
  }

  private transferMatchesBranch(
    filterId: string,
    branchName?: string,
    branchId?: string,
    locationBlob?: string,
    branches?: Array<{ id: string; name: string }>,
  ): boolean {
    if (!filterId || filterId === 'all') {
      return true;
    }
    if (
      matchesBranchFilter(filterId, branchName, branchId, branches) ||
      matchesBranchFilter(filterId, locationBlob, branchId, branches)
    ) {
      return true;
    }
    const branch = branches?.find((row) => row.id === filterId);
    const name = (branch?.name || '').toLowerCase().trim();
    const blob = (locationBlob || '').toLowerCase();
    return !!name && !!blob && blob.includes(name);
  }

  private transferInvolvesBranch(
    filterId: string,
    transfer: StockTransfer,
    branches?: Array<{ id: string; name: string }>,
  ): boolean {
    const fromBlob = `${transfer.fromBranch || ''} ${transfer.fromLocationName || ''} ${transfer.fromLocation || ''}`;
    const toBlob = `${transfer.toBranch || ''} ${transfer.toLocationName || ''} ${transfer.toLocation || ''}`;
    return (
      this.transferMatchesBranch(
        filterId,
        transfer.fromBranch,
        transfer.fromBranchId,
        fromBlob,
        branches,
      ) ||
      this.transferMatchesBranch(
        filterId,
        transfer.toBranch,
        transfer.toBranchId,
        toBlob,
        branches,
      )
    );
  }

  clearFilters(): void {
    this.search.set('');
    const branches = this.branches();
    const defaultFilter =
      branches.length && isBranchFilterLocked(this.auth)
        ? defaultBranchFilterId(this.auth, branches)
        : 'all';
    this.fromBranchFilter.set(defaultFilter);
    this.toBranchFilter.set('all');
    this.statusFilter.set('all');
    this.page.set(1);
  }

  getFromBranch(row: StockTransfer): string {
    return row.fromBranch || row.fromLocation?.split(' / ')[0] || '—';
  }

  getToBranch(row: StockTransfer): string {
    return row.toBranch || row.toLocation?.split(' / ')[0] || '—';
  }

  getFromStorage(row: StockTransfer): string {
    return (
      row.fromLocationName ||
      row.fromLocation?.split(' / ').slice(1).join(' / ') ||
      '—'
    );
  }

  getToStorage(row: StockTransfer): string {
    return (
      row.toLocationName ||
      row.toLocation?.split(' / ').slice(1).join(' / ') ||
      '—'
    );
  }

  getItemsLabel(row: StockTransfer): string {
    if (row.itemName?.trim()) return row.itemName;
    const count = Number(row.items) || 0;
    return count > 0 ? `${count} item(s)` : '—';
  }

  statusLabel(status: TransferStatus): string {
    const map: Record<TransferStatus, string> = {
      pending: 'Pending',
      'in-transit': 'In Transit',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return map[status] || status;
  }

  onSearchChange(): void {
    this.page.set(1);
  }

  onFilterChange(): void {
    this.page.set(1);
  }

  goToPage(p: number): void {
    if (p >= 1 && p <= this.totalPages()) this.page.set(p);
  }

  openTransferModal(): void {
    this.editingTransfer.set(null);
    const branches = this.fromBranches();
    const defaultBranchId =
      defaultBranchFilterId(this.auth, branches) !== 'all'
        ? defaultBranchFilterId(this.auth, branches)
        : branches[0]?.id || '';
    const firstBranch =
      branches.find((b) => b.id === defaultBranchId) ?? branches[0];
    const branchId = firstBranch?.id || '';
    const branchLocs = branchId
      ? this.locations().filter((l) => l.branchId === branchId)
      : [];
    const fromLocationId = branchLocs[0]?.id || '';
    const destBranches = this.toBranches().filter((b) => b.id !== branchId);
    const toBranchId = destBranches[0]?.id || branchId;
    const toLocs = toBranchId
      ? this.locations().filter((l) => l.branchId === toBranchId)
      : [];
    const toLocationId =
      toLocs.find((l) => !(toBranchId === branchId && l.id === fromLocationId))
        ?.id ||
      toLocs[0]?.id ||
      '';

    this.form.set({
      fromBranchId: branchId,
      fromLocationId,
      toBranchId,
      toLocationId,
      transferDate: new Date().toISOString().slice(0, 10),
      status: 'pending',
      notes: '',
      referenceNumber: '',
    });
    this.lines.set([]);
    this.selectedItemId.set('');
    this.selectedQty.set(1);
    this.itemSearch.set('');
    this.showModal.set(true);
    document.body.style.overflow = 'hidden';

    if (!environment.useMockData && (!branches.length || !branchLocs.length)) {
      this.settings.refreshFromApi();
    }
  }

  openEditModal(row: StockTransfer): void {
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) {
      this.showToastMessage(
        'This transfer has no valid database id. Refresh the page, then try Edit again.',
        'error',
      );
      return;
    }
    this.editingTransfer.set(row);
    const fromBranchId =
      this.fromBranches().find(
        (b) => b.name === row.fromBranch || b.id === row.fromBranch,
      )?.id ||
      this.toBranches().find(
        (b) => b.name === row.fromBranch || b.id === row.fromBranch,
      )?.id ||
      '';
    const toBranchId =
      this.toBranches().find(
        (b) => b.name === row.toBranch || b.id === row.toBranch,
      )?.id || '';
    const fromLocationId =
      this.locations().find(
        (l) =>
          l.name === row.fromLocationName ||
          l.id === row.fromLocationName ||
          (fromBranchId &&
            l.branchId === fromBranchId &&
            row.fromLocation?.includes(l.name)),
      )?.id || '';
    const toLocationId =
      this.locations().find(
        (l) =>
          l.name === row.toLocationName ||
          l.id === row.toLocationName ||
          (toBranchId &&
            l.branchId === toBranchId &&
            row.toLocation?.includes(l.name)),
      )?.id || '';

    this.form.set({
      fromBranchId,
      fromLocationId,
      toBranchId,
      toLocationId,
      transferDate: (row.transferDate || '').slice(0, 10),
      status: row.status,
      notes: '',
      referenceNumber: '',
    });
    this.lines.set([]);
    this.showModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingTransfer.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeModal();
    }
  }

  updateForm<K extends keyof ReturnType<typeof this.form>>(
    field: K,
    value: ReturnType<typeof this.form>[K],
  ): void {
    this.form.update((f) => ({ ...f, [field]: value }));
  }

  onFromBranchChange(branchId: string): void {
    const locs = this.locations().filter((l) => l.branchId === branchId);
    this.form.update((f) => ({
      ...f,
      fromBranchId: branchId,
      fromLocationId: locs[0]?.id || '',
    }));
    this.lines.set([]);
    this.selectedItemId.set('');
  }

  onToBranchChange(branchId: string): void {
    const locs = this.locations().filter((l) => l.branchId === branchId);
    this.form.update((f) => ({
      ...f,
      toBranchId: branchId,
      toLocationId: locs[0]?.id || '',
    }));
  }

  onFromLocationChange(locationId: string): void {
    this.form.update((f) => ({ ...f, fromLocationId: locationId }));
    this.lines.set([]);
    this.selectedItemId.set('');
  }

  onSelectedQtyChange(raw: unknown): void {
    const parsed = parsePositiveInt(raw, 'Quantity');
    if (!parsed.ok) {
      this.showToastMessage(
        parsed.message ?? 'Enter a valid quantity',
        'error',
      );
      return;
    }
    this.selectedQty.set(parsed.value);
  }

  addLine(): void {
    const itemId = this.selectedItemId();
    const parsed = parsePositiveInt(this.selectedQty(), 'Quantity');
    if (!parsed.ok) {
      this.showToastMessage(
        parsed.message ?? 'Enter a valid quantity',
        'error',
      );
      return;
    }
    const qty = parsed.value;
    const stock = this.availableItems().find((s) => s.id === itemId);
    if (!stock) {
      this.showToastMessage(
        'Select an item with available stock at the from location.',
        'error',
      );
      return;
    }

    const numericId = Number(stock.id);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      this.showToastMessage(
        'That item has no valid database id. Refresh the page (or re-login) so Register Items reloads from the API.',
        'error',
      );
      return;
    }

    const f = this.form();
    const fromLocation = this.locations().find(
      (l) => l.id === f.fromLocationId,
    );
    const liveAvailable = this.inventory.availableQuantityAtLocation(
      stock.id,
      f.fromBranchId,
      f.fromLocationId,
      fromLocation?.name,
    );
    const check = assertCanTake(liveAvailable, qty, stock.name);
    if (!check.ok) {
      this.showToastMessage(
        check.message ?? `Only ${liveAvailable} available.`,
        'error',
      );
      return;
    }

    this.lines.update((list) => [
      ...list,
      {
        itemId: String(numericId),
        code: stock.code,
        name: stock.name,
        quantity: qty,
        available: liveAvailable,
      },
    ]);
    this.selectedItemId.set('');
    this.selectedQty.set(1);
  }

  removeLine(index: number): void {
    this.lines.update((list) => list.filter((_, i) => i !== index));
  }

  saveTransfer(): void {
    if (this.saving()) return;
    const editing = this.editingTransfer();
    if (editing) {
      this.saveEdit(editing);
      return;
    }
    this.saveCreate();
  }

  private saveCreate(): void {
    const f = this.form();
    const lines = this.lines();
    const fromBranchID = Number(f.fromBranchId);
    const toBranchID = Number(f.toBranchId);
    const fromLocationID = Number(f.fromLocationId);
    const toLocationID = Number(f.toLocationId);

    if (!fromBranchID || !toBranchID || !fromLocationID || !toLocationID) {
      const missing: string[] = [];
      if (!fromBranchID) missing.push('From Branch');
      if (!fromLocationID) missing.push('From Location');
      if (!toBranchID) missing.push('To Branch');
      if (!toLocationID) missing.push('To Location');
      this.showToastMessage(`Select ${missing.join(', ')}.`, 'error');
      return;
    }

    const allowedFrom = this.fromBranches().some(
      (b) => b.id === f.fromBranchId,
    );
    if (!allowedFrom) {
      this.showToastMessage(
        'From Branch must be a branch you have access to.',
        'error',
      );
      return;
    }

    const allowedTo = this.toBranches().some((b) => b.id === f.toBranchId);
    if (!allowedTo) {
      this.showToastMessage(
        'To Branch must be a branch in your company.',
        'error',
      );
      return;
    }

    const fromLoc = this.locations().find((l) => l.id === f.fromLocationId);
    const toLoc = this.locations().find((l) => l.id === f.toLocationId);
    if (fromLoc && fromLoc.branchId !== f.fromBranchId) {
      this.showToastMessage(
        'From location does not belong to the selected From branch.',
        'error',
      );
      return;
    }
    if (toLoc && toLoc.branchId !== f.toBranchId) {
      this.showToastMessage(
        'To location does not belong to the selected To branch.',
        'error',
      );
      return;
    }

    if (fromBranchID === toBranchID && fromLocationID === toLocationID) {
      this.showToastMessage(
        'From and To locations must be different.',
        'error',
      );
      return;
    }
    if (!lines.length) {
      this.showToastMessage('Add at least one item to transfer.', 'error');
      return;
    }

    const invalidLine = lines.find(
      (l) => !Number.isFinite(Number(l.itemId)) || Number(l.itemId) <= 0,
    );
    if (invalidLine) {
      this.showToastMessage(
        `Item "${invalidLine.name}" has an invalid id. Refresh the page and add items again.`,
        'error',
      );
      return;
    }

    const fromBranch = this.fromBranches().find((b) => b.id === f.fromBranchId);
    const toBranch = this.toBranches().find((b) => b.id === f.toBranchId);
    const fromLocation = this.locations().find(
      (l) => l.id === f.fromLocationId,
    );
    const toLocation = this.locations().find((l) => l.id === f.toLocationId);

    this.saving.set(true);
    this.inventory.loadItemMasterAndStock().subscribe(() => {
      for (const line of lines) {
        const parsed = parsePositiveInt(line.quantity, line.name);
        if (!parsed.ok) {
          this.saving.set(false);
          this.showToastMessage(
            parsed.message ?? 'Enter a valid quantity',
            'error',
          );
          return;
        }
        const available = this.inventory.availableQuantityAtLocation(
          line.itemId,
          f.fromBranchId,
          f.fromLocationId,
          fromLocation?.name,
        );
        const check = assertCanTake(available, parsed.value, line.name);
        if (!check.ok) {
          this.saving.set(false);
          this.showToastMessage(
            check.message ??
              `Only ${available} available at the from location.`,
            'error',
          );
          return;
        }
      }

      // Cross-branch: dispatch as In Transit; receiving branch confirms.
      // Same-branch location move may complete immediately.
      const dispatchStatus: TransferStatus =
        fromBranchID === toBranchID ? 'completed' : 'in-transit';

      this.inventory
        .createStockTransferViaApi({
          fromBranchID,
          toBranchID,
          fromLocationID,
          toLocationID,
          transferDate: f.transferDate,
          status: dispatchStatus,
          notes: f.notes || null,
          referenceNumber: f.referenceNumber || null,
          fromBranch: fromBranch?.name,
          toBranch: toBranch?.name,
          fromLocation: fromLocation?.name,
          toLocation: toLocation?.name,
          lines: lines.map((l) => ({
            inventoryItemID: Number(l.itemId),
            quantity: l.quantity,
            itemName: l.name,
          })),
        })
        .subscribe((result) => {
          this.saving.set(false);
          if (!result.success) {
            this.showToastMessage(
              result.message || 'Unable to create stock transfer.',
              'error',
            );
            return;
          }
          this.showToastMessage(
            dispatchStatus === 'completed'
              ? `Transfer completed. Stock moved to ${toLocation?.name || 'the destination location'}.`
              : `Transfer sent to ${toBranch?.name || 'the receiving branch'}. They must confirm receipt on Stock Transfers before stock arrives.`,
            'success',
          );
          this.closeModal();
        });
    });
  }

  private saveEdit(row: StockTransfer): void {
    const f = this.form();
    if (!row.id) {
      this.showToastMessage('Invalid transfer id.', 'error');
      return;
    }

    if (f.status === 'completed' && !this.canConfirmReceipt(row)) {
      this.showToastMessage(
        'Only users at the receiving branch can confirm this transfer has been received.',
        'error',
      );
      return;
    }

    this.saving.set(true);
    this.inventory
      .updateStockTransferViaApi(row.id, {
        status: f.status,
        notes: f.notes || null,
        transferDate: f.transferDate || undefined,
        referenceNumber: f.referenceNumber || null,
      })
      .subscribe((result) => {
        this.saving.set(false);
        if (!result.success) {
          this.showToastMessage(
            result.message || 'Unable to update transfer.',
            'error',
          );
          return;
        }
        this.showToastMessage(
          f.status === 'completed'
            ? 'Receipt confirmed. Stock is now at the destination.'
            : `Transfer updated to ${this.statusLabel(f.status)}.`,
          'success',
        );
        this.closeModal();
      });
  }

  /** Receiving branch (home or UserBranches) may confirm completion. */
  canConfirmReceipt(row: StockTransfer): boolean {
    const toId = Number(row.toBranchId);
    if (!Number.isFinite(toId) || toId <= 0) {
      // Fallback: match by branch name when id is missing from older payloads.
      const toName = (row.toBranch || '').trim().toLowerCase();
      if (!toName) {
        return false;
      }
      const homeName = (
        this.fromBranches().find(
          (b) => Number(b.id) === this.auth.currentBranchId(),
        )?.name || ''
      )
        .trim()
        .toLowerCase();
      return !!homeName && homeName === toName;
    }
    return this.auth.allowedBranchIds().includes(toId);
  }

  canDispatchCancel(row: StockTransfer): boolean {
    // Sender or receiver may cancel while not completed; completed reverse is receiver-side.
    if (row.status === 'completed') {
      return this.canConfirmReceipt(row);
    }
    const fromId = Number(row.fromBranchId);
    if (Number.isFinite(fromId) && fromId > 0) {
      return (
        this.auth.allowedBranchIds().includes(fromId) ||
        this.canConfirmReceipt(row)
      );
    }
    return true;
  }

  completeTransfer(row: StockTransfer, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.canConfirmReceipt(row)) {
      this.showToastMessage(
        'Only the receiving branch can confirm this transfer has been received.',
        'error',
      );
      return;
    }
    if (
      !confirmAction(
        'Confirm this transfer has been received? Stock will move into your branch location.',
      )
    ) {
      return;
    }
    this.quickStatus(
      row,
      'completed',
      'Receipt confirmed. Stock is now at the destination.',
    );
  }

  cancelTransfer(row: StockTransfer, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    const msg =
      row.status === 'completed'
        ? 'Cancel this completed transfer and restore stock quantities?'
        : 'Cancel this transfer?';
    if (!confirmAction(msg)) return;
    this.quickStatus(row, 'cancelled', 'Transfer cancelled.');
  }

  private quickStatus(
    row: StockTransfer,
    status: TransferStatus,
    successMessage: string,
  ): void {
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) {
      this.showToastMessage(
        'This transfer has no valid database id. Click Clear filters, refresh the page, then try again.',
        'error',
      );
      return;
    }
    if (this.saving()) {
      this.showToastMessage(
        'Please wait — another save is still in progress.',
        'info',
      );
      return;
    }

    this.saving.set(true);
    this.inventory.updateStockTransferViaApi(String(id), { status }).subscribe({
      next: (result) => {
        this.saving.set(false);
        if (!result.success) {
          this.showToastMessage(
            result.message || 'Unable to update transfer.',
            'error',
          );
          return;
        }
        this.showToastMessage(successMessage, 'success');
      },
      error: () => {
        this.saving.set(false);
        this.showToastMessage('Unable to update transfer.', 'error');
      },
    });
  }

  exportData(): void {
    const rows = this.filteredTransfers();
    if (!rows.length) {
      this.showToastMessage('No transfers match the current filters.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `stock-transfers-${new Date().toISOString().slice(0, 10)}`,
      rows.map((r) => ({
        'Transfer ID': r.transferNumber,
        'From Branch': this.getFromBranch(r),
        'From Storage': this.getFromStorage(r),
        'To Branch': this.getToBranch(r),
        'To Storage': this.getToStorage(r),
        Items: this.getItemsLabel(r),
        Date: this.formatDate(r.transferDate),
        Status: this.statusLabel(r.status),
      })),
    );
  }

  showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
  ): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    this.toastTimer = setTimeout(() => this.closeToast(), 5000);
  }

  closeToast(): void {
    this.showToast.set(false);
  }
}
